import React, { useEffect, useState } from 'react';
import useFieldTracker from '../../hooks/useFieldTracker';
import VgsCollectValue from '../VgsInput/VgsCollectValue';
import { generateFieldName } from '../../util/fields';
import BusinessTypeDropdowns from '../CreateKybKyc/Kyb/BusinessTypeDropdowns';
import { phoneValidation } from '../VgsInput/validations';
import AddressCollect from '../Address/AddressCollect';
import { FieldError } from '../VgsInput/field';
import { WEBSITE_VALIDATIONS } from './const';

export default function KybBioInput({
  form,
  entityName,
  onValidChange,
  onBusinessTypeChange,
  onBusinessStructureChange,
  onAddressRegionChange,
  fieldErrors,
}: {
  form: VGSForm<unknown>;
  entityName?: string;
  onValidChange: (isValid: boolean) => void;
  onBusinessTypeChange: (type: string) => void;
  onBusinessStructureChange: (type: string) => void;
  onAddressRegionChange: (region: string) => void;
  fieldErrors: FieldError[];
}): React.JSX.Element {
  const rootName = generateFieldName(entityName, 'kyb');
  const addressName = generateFieldName(entityName, 'kybAddress');
  const [emptyFields, onEmptyChange] = useFieldTracker([
    'kybLegalName',
    'kybCustomerFacingName',
    'kybUrl',
    'kybPhoneNumber',
    'kybTaxId',
  ]);

  const [invalidFields, onInvalidChange] = useFieldTracker([addressName]);

  const [isValid, setIsValid] = useState(false);
  const sharedProps = {
    type: 'text' as VgsFieldType,
    required: true,
    showRequiredAsterisk: true,
    onEmptyChange,
    onInvalidChange,
    form,
    fieldErrors,
  };

  useEffect(() => {
    const newValidity = emptyFields.length === 0 && invalidFields.length === 0;
    if (newValidity !== isValid) {
      setIsValid(newValidity);
      onValidChange(newValidity);
    }
  }, [emptyFields, invalidFields, isValid, onValidChange]);

  return (
    <form>
      <div className='md:flex'>
        <div className='md:w-full my-2'>
          <VgsCollectValue
            name='kybCustomerFacingName'
            label='Payable Name'
            {...sharedProps}
          />
        </div>
      </div>

      <div className='md:flex mb-5'>
        <div className='md:w-full'>
          <p className='page-text-secondary'>
            This information must be identical to IRS documentation and should
            be the information used to conduct business. This is the payable
            name that your check or direct deposit will be issued to.
          </p>
        </div>
      </div>
      <div className='md:flex'>
        <div className='md:w-full my-2'>
          <VgsCollectValue
            name='kybLegalName'
            label='Business Name'
            {...sharedProps}
          />
        </div>
      </div>
      <div className='md:flex mb-5'>
        <BusinessTypeDropdowns
          onTypeChange={onBusinessTypeChange}
          onStructureChange={onBusinessStructureChange}
        />
      </div>

      <div className='md:flex mb-5'>
        <div className='md:w-full'>
          <VgsCollectValue
            name='kybUrl'
            label='Website'
            validations={WEBSITE_VALIDATIONS}
            {...sharedProps}
          />
        </div>
      </div>

      <div className='md:flex mb-5'>
        <div className='md:w-full'>
          <VgsCollectValue
            name='kybPhoneNumber'
            label='Phone Number'
            placeholder='(XXX) XXX-XXXX'
            maskInput={{
              mask: '(999) 999-9999',
            }}
            validations={[phoneValidation]}
            {...sharedProps}
          />
        </div>
      </div>

      <div className='md:flex mb-5'>
        <div className='md:w-full'>
          <VgsCollectValue
            name='kybTaxId'
            label='Tax ID'
            placeholder='XX-XXXXXXX'
            maskInput={{
              mask: 'XX-XXXXXXX',
              maskChar: null,
              formatChar: { X: '[0-9]' },
            }}
            validations={[/^\d{2}-\d{7}$/]}
            {...sharedProps}
          />
        </div>
      </div>
      <AddressCollect
        entityName={rootName}
        onRegionChange={onAddressRegionChange}
        onInvalidChange={sharedProps.onInvalidChange}
        form={form}
        fieldErrors={fieldErrors}
      />
    </form>
  );
}
